import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import type { PlatformCoverageSearch } from '~/features/platform/types/platform-coverage.types';
import {
  parsePlatformCoverageSearch,
  replacesHistory,
} from '~/features/platform/utils/platform-coverage-search';
import { requireSystemSubsidiaryAdmin } from '~/utils/guards';

const PlatformCoveragePage = lazy(() => import('~/features/platform/pages/platform-coverage-page'));

const requirePlatformAdmin = requireSystemSubsidiaryAdmin();

// Lily AI platform admins only — the Schedules guard and sidebar rule (`canAccessNavPath('/coverage')`).
// The integration-service report routes keep their own Lily-admin guard (403 otherwise). See
// docs/platform-coverage.md.
export const Route = createFileRoute('/_authenticated/_tenant/coverage')({
  beforeLoad: () => {
    requirePlatformAdmin();
  },
  validateSearch: (search: Record<string, unknown>): PlatformCoverageSearch =>
    parsePlatformCoverageSearch(search),
  component: RouteComponent,
});

/**
 * Binds the page's filter / sort / page state to the URL. Tab and subsidiary / connector / issue
 * changes push, so Back steps through them; typed search, sort and paging replace.
 */
function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <PlatformCoveragePage
        search={search}
        onSearchChange={(next) => {
          const parsed = parsePlatformCoverageSearch({ ...next });
          void navigate({ search: parsed, replace: replacesHistory(search, parsed) });
        }}
      />
    </Suspense>
  );
}
